/**
 * Domain logic for the Advanced page's Corrections tab (legacy Delphi
 * "Manipulare" form): the reversal operations, the retrospective edits to a
 * contract's terms, and the two tools that are not contract-scoped - restating
 * a day's exchange rate and reversing a consignment sale.
 */

export interface CorrectionEligibility {
    canCancelContract: boolean;
    canReversePayment: boolean;
    canReverseExtension: boolean;
    canReverseGoodsSale: boolean;
    canRestoreToActive: boolean;
}

export interface CorrectionInvoice {
    id: number;
    invoiceNumber: string | null;
    invoiceDate: string | null;
    invoiceType: string | null;
    amount: number;
}

export interface CorrectionExtension {
    id: number;
    extensionNumber: number | null;
    extensionDate: string | null;
    amountRon: number;
}

export interface ContractCorrectionsSummary {
    contractNr: number;
    contractNumber: string | null;
    clientName: string | null;
    status: string | null;
    contractDate: string | null;
    expirationDate: string | null;
    loanAmountRon: number;
    dailyInterestRate: number;
    durationDays: number | null;
    originalDueDate: string | null;
    standardDiscountPercent: number | null;
    invoices: CorrectionInvoice[];
    extensions: CorrectionExtension[];
    eligibility: CorrectionEligibility;
}

export type CorrectionActionKey =
    | 'cancelContract'
    | 'reversePayment'
    | 'reverseExtension'
    | 'reverseGoodsSale'
    | 'restoreToActive';

export interface CorrectionAction {
    key: CorrectionActionKey;
    endpoint: string;
    enabled: boolean;
}

/** The reversal operations offered on the Corrections tab, with availability. */
export function getCorrectionActions(
    summary: ContractCorrectionsSummary | null,
): CorrectionAction[] {
    const eligibility = summary?.eligibility;
    return [
        {
            key: 'cancelContract',
            endpoint: '/corrections/cancel-contract',
            enabled: eligibility?.canCancelContract ?? false,
        },
        {
            key: 'reversePayment',
            endpoint: '/corrections/reverse-payment',
            enabled: eligibility?.canReversePayment ?? false,
        },
        {
            key: 'reverseExtension',
            endpoint: '/corrections/reverse-extension',
            enabled: eligibility?.canReverseExtension ?? false,
        },
        {
            key: 'reverseGoodsSale',
            endpoint: '/corrections/reverse-goods-sale',
            enabled: eligibility?.canReverseGoodsSale ?? false,
        },
        {
            key: 'restoreToActive',
            endpoint: '/corrections/restore-to-active',
            enabled: eligibility?.canRestoreToActive ?? false,
        },
    ];
}

/** Map a contract status code to the i18n suffix used for display. */
export function describeContractStatus(status: string | null | undefined): string {
    switch (status ?? '') {
        case '':
            return 'active';
        case '!':
            return 'passive';
        case '!!':
            return 'expired';
        case 'INV':
            return 'inStock';
        case 'EXP':
            return 'sold';
        case 'OK':
            return 'paid';
        case 'ANL':
            return 'cancelled';
        default:
            return 'unknown';
    }
}

/** Round a currency amount to whole RON for display, per project display rules. */
export function formatRonAmount(amount: number | null | undefined): string {
    const value = Number(amount ?? 0);
    return Number.isFinite(value) ? Math.round(value).toString() : '0';
}

/** The contract terms the Corrections tab can restate in place. */
export type ContractTermKey = 'setDailyCommission' | 'setDuration' | 'setStandardDiscount';

export interface ContractTermEditor {
    key: ContractTermKey;
    endpoint: string;
    /** Request body field that carries the new value. */
    field: 'dailyInterestRate' | 'durationDays' | 'standardDiscountPercent';
    /** Whole numbers only, matching the backend validator. */
    integerOnly: boolean;
    min: number;
    max: number;
}

const TERM_EDITORS: ContractTermEditor[] = [
    {
        key: 'setDailyCommission',
        endpoint: '/corrections/set-daily-commission',
        field: 'dailyInterestRate',
        integerOnly: false,
        min: 0,
        max: 100,
    },
    {
        key: 'setDuration',
        endpoint: '/corrections/set-duration',
        field: 'durationDays',
        integerOnly: true,
        min: 1,
        max: 3650,
    },
    {
        key: 'setStandardDiscount',
        endpoint: '/corrections/set-standard-discount',
        field: 'standardDiscountPercent',
        integerOnly: true,
        min: 0,
        max: 100,
    },
];

/** The retrospective term edits offered once a contract is loaded. */
export function getContractTermEditors(): ContractTermEditor[] {
    return TERM_EDITORS;
}

/** The value an editor's field currently holds, as text for its input. */
export function currentTermValue(
    summary: ContractCorrectionsSummary | null,
    key: ContractTermKey,
): string {
    if (!summary) {
        return '';
    }
    switch (key) {
        case 'setDailyCommission':
            return String(summary.dailyInterestRate);
        case 'setDuration':
            return String(summary.durationDays ?? '');
        case 'setStandardDiscount':
            return String(summary.standardDiscountPercent ?? 0);
    }
}

/**
 * Parse an editor's input.
 *
 * @returns The numeric value, or `null` when the text is not a number the
 * backend would accept.
 */
export function parseTermValue(editor: ContractTermEditor, raw: string): number | null {
    const trimmed = raw.trim();
    if (trimmed === '') {
        return null;
    }
    const value = Number(trimmed);
    if (!Number.isFinite(value) || value < editor.min || value > editor.max) {
        return null;
    }
    if (editor.integerOnly && !Number.isInteger(value)) {
        return null;
    }
    return value;
}

/**
 * The due date a duration implies, mirroring the backend (and the legacy
 * `Edit10KeyPress`): a one-day contract falls due the next day, longer ones
 * count the contract date as their first day.
 */
export function computeDueDatePreview(
    contractDate: string | null | undefined,
    durationDays: number | null,
): string | null {
    if (!contractDate || durationDays === null || !Number.isInteger(durationDays)) {
        return null;
    }
    if (durationDays < 1) {
        return null;
    }
    const start = new Date(`${contractDate.slice(0, 10)}T00:00:00Z`);
    if (Number.isNaN(start.getTime())) {
        return null;
    }
    start.setUTCDate(start.getUTCDate() + (durationDays === 1 ? 1 : durationDays - 1));
    return start.toISOString().slice(0, 10);
}

/**
 * Parse the EUR/RON rate typed into the exchange-rate tool.
 *
 * @returns The rate, or `null` when it is not a positive number below 1000.
 */
export function parseExchangeRateInput(raw: string): number | null {
    const trimmed = raw.trim().replace(',', '.');
    if (trimmed === '') {
        return null;
    }
    const value = Number(trimmed);
    return Number.isFinite(value) && value > 0 && value < 1000 ? value : null;
}

/** Normalise a scanned consignment barcode (receipt number + item code). */
export function normalizeConsignmentCode(raw: string): string {
    return raw.trim().toUpperCase();
}
